import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';

// RevenueCat API keys from environment
const REVENUECAT_IOS_KEY = process.env.REACT_APP_REVENUECAT_IOS_KEY || '';
const REVENUECAT_ANDROID_KEY = process.env.REACT_APP_REVENUECAT_ANDROID_KEY || '';

// Mock mode for local testing (set REACT_APP_REVENUECAT_MOCK=true in .env.mobile)
const MOCK_MODE = process.env.REACT_APP_REVENUECAT_MOCK === 'true';

// Entitlement identifier configured in RevenueCat dashboard
const PREMIUM_ENTITLEMENT = 'premium';

// Types for RevenueCat (simplified for our use case)
interface Package {
  identifier: string;
  packageType: string;
  product: {
    identifier: string;
    priceString: string;
    price: number;
    title: string;
    description: string;
  };
}

interface CustomerInfo {
  entitlements: {
    active: Record<string, unknown>;
  };
}

interface RevenueCatState {
  isInitialized: boolean;
  isPremium: boolean;
  customerInfo: CustomerInfo | null;
  packages: Package[];
  isLoading: boolean;
  error: string | null;
}

// Dynamic import to avoid issues on web
let Purchases: any = null;
let LOG_LEVEL: any = null;

const loadRevenueCat = async () => {
  if (Purchases) return { Purchases, LOG_LEVEL };

  try {
    const module = await import('@revenuecat/purchases-capacitor');
    Purchases = module.Purchases;
    LOG_LEVEL = module.LOG_LEVEL;
    return { Purchases, LOG_LEVEL };
  } catch (error) {
    console.warn('RevenueCat not available:', error);
    return { Purchases: null, LOG_LEVEL: null };
  }
};

export function useRevenueCat(userId?: string) {
  const [state, setState] = useState<RevenueCatState>({
    isInitialized: false,
    isPremium: false,
    customerInfo: null,
    packages: [],
    isLoading: true,
    error: null,
  });

  // Initialize RevenueCat
  useEffect(() => {
    const initRevenueCat = async () => {
      const platform = Capacitor.getPlatform();

      // Only initialize on native platforms
      if (platform === 'web') {
        console.log('RevenueCat: Web platform, skipping initialization');
        setState(prev => ({
          ...prev,
          isLoading: false,
          isInitialized: true,
        }));
        return;
      }

      // Mock mode for local testing
      if (MOCK_MODE) {
        console.log('RevenueCat: MOCK MODE enabled');
        setState({
          isInitialized: true,
          isPremium: false,
          customerInfo: null,
          packages: [
            {
              identifier: '$rc_monthly',
              packageType: 'MONTHLY',
              product: {
                identifier: 'lilhero.premium.monthly',
                priceString: '7,99 €',
                price: 7.99,
                title: 'LilHero Premium Mensuel',
                description: 'Accès illimité pendant 1 mois',
              },
            },
            {
              identifier: '$rc_annual',
              packageType: 'ANNUAL',
              product: {
                identifier: 'lilhero.premium.yearly',
                priceString: '49,99 €',
                price: 49.99,
                title: 'LilHero Premium Annuel',
                description: 'Accès illimité pendant 1 an',
              },
            },
          ],
          isLoading: false,
          error: null,
        });
        return;
      }

      const apiKey = platform === 'ios' ? REVENUECAT_IOS_KEY : REVENUECAT_ANDROID_KEY;

      if (!apiKey) {
        console.warn('RevenueCat: No API key configured for platform:', platform);
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: 'RevenueCat API key not configured',
        }));
        return;
      }

      try {
        // Load RevenueCat dynamically
        const { Purchases: RC, LOG_LEVEL: LL } = await loadRevenueCat();

        if (!RC) {
          throw new Error('RevenueCat SDK not available');
        }

        // Configure RevenueCat
        await RC.setLogLevel({ level: LL.DEBUG });

        await RC.configure({
          apiKey,
          appUserID: userId || undefined,
        });

        console.log('RevenueCat: Initialized successfully');

        // Get customer info
        const customerInfoResult = await RC.getCustomerInfo();
        const customerInfo = customerInfoResult.customerInfo;
        const isPremium = customerInfo?.entitlements?.active?.[PREMIUM_ENTITLEMENT] !== undefined;

        // Get offerings
        const offeringsResult = await RC.getOfferings();
        const currentOffering = offeringsResult?.offerings?.current;
        const packages = currentOffering?.availablePackages || [];

        setState({
          isInitialized: true,
          isPremium,
          customerInfo,
          packages,
          isLoading: false,
          error: null,
        });

        console.log('RevenueCat: Customer is premium:', isPremium);
        console.log('RevenueCat: Available packages:', packages.length);
      } catch (error: any) {
        console.error('RevenueCat: Initialization error:', error);
        setState(prev => ({
          ...prev,
          isLoading: false,
          isInitialized: true, // Mark as initialized even on error so UI doesn't hang
          error: error.message || 'Failed to initialize RevenueCat',
        }));
      }
    };

    initRevenueCat();
  }, [userId]);

  // Login user (link anonymous purchases to account)
  const login = useCallback(async (appUserID: string) => {
    const platform = Capacitor.getPlatform();
    if (platform === 'web') return;

    try {
      const { Purchases: RC } = await loadRevenueCat();
      if (!RC) return;

      const result = await RC.logIn({ appUserID });
      const customerInfo = result.customerInfo;
      const isPremium = customerInfo?.entitlements?.active?.[PREMIUM_ENTITLEMENT] !== undefined;

      setState(prev => ({
        ...prev,
        customerInfo,
        isPremium,
      }));

      console.log('RevenueCat: User logged in:', appUserID);
    } catch (error: any) {
      console.error('RevenueCat: Login error:', error);
      throw error;
    }
  }, []);

  // Logout user
  const logout = useCallback(async () => {
    const platform = Capacitor.getPlatform();
    if (platform === 'web') return;

    try {
      const { Purchases: RC } = await loadRevenueCat();
      if (!RC) return;

      const result = await RC.logOut();
      const customerInfo = result.customerInfo;
      const isPremium = customerInfo?.entitlements?.active?.[PREMIUM_ENTITLEMENT] !== undefined;

      setState(prev => ({
        ...prev,
        customerInfo,
        isPremium,
      }));

      console.log('RevenueCat: User logged out');
    } catch (error: any) {
      console.error('RevenueCat: Logout error:', error);
    }
  }, []);

  // Purchase a package
  const purchasePackage = useCallback(async (pkg: Package) => {
    const platform = Capacitor.getPlatform();
    if (platform === 'web') {
      return { success: false, cancelled: false, error: 'Purchases not available on web' };
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    // Mock mode - simulate successful purchase
    if (MOCK_MODE) {
      console.log('RevenueCat: MOCK purchase for', pkg.identifier);
      await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate delay
      setState(prev => ({
        ...prev,
        isPremium: true,
        isLoading: false,
      }));
      return { success: true, isPremium: true, cancelled: false };
    }

    try {
      const { Purchases: RC } = await loadRevenueCat();
      if (!RC) {
        throw new Error('RevenueCat not available');
      }

      const result = await RC.purchasePackage({ aPackage: pkg });
      const customerInfo = result.customerInfo;
      const isPremium = customerInfo?.entitlements?.active?.[PREMIUM_ENTITLEMENT] !== undefined;

      setState(prev => ({
        ...prev,
        customerInfo,
        isPremium,
        isLoading: false,
      }));

      console.log('RevenueCat: Purchase successful, isPremium:', isPremium);
      return { success: true, isPremium, cancelled: false };
    } catch (error: any) {
      console.error('RevenueCat: Purchase error:', error);

      // Check if user cancelled
      const userCancelled = error.code === 'PURCHASE_CANCELLED_ERROR' ||
                           error.code === 1 ||
                           error.userCancelled === true;

      setState(prev => ({
        ...prev,
        isLoading: false,
        error: userCancelled ? null : (error.message || 'Purchase failed'),
      }));

      return { success: false, cancelled: userCancelled, error: error.message };
    }
  }, []);

  // Restore purchases
  const restorePurchases = useCallback(async () => {
    const platform = Capacitor.getPlatform();
    if (platform === 'web') {
      return { success: false, isPremium: false, error: 'Restore not available on web' };
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const { Purchases: RC } = await loadRevenueCat();
      if (!RC) {
        throw new Error('RevenueCat not available');
      }

      const result = await RC.restorePurchases();
      const customerInfo = result.customerInfo;
      const isPremium = customerInfo?.entitlements?.active?.[PREMIUM_ENTITLEMENT] !== undefined;

      setState(prev => ({
        ...prev,
        customerInfo,
        isPremium,
        isLoading: false,
      }));

      console.log('RevenueCat: Restore successful, isPremium:', isPremium);
      return { success: true, isPremium };
    } catch (error: any) {
      console.error('RevenueCat: Restore error:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Restore failed',
      }));
      return { success: false, isPremium: false, error: error.message };
    }
  }, []);

  // Refresh customer info
  const refreshCustomerInfo = useCallback(async () => {
    const platform = Capacitor.getPlatform();
    if (platform === 'web') return;

    try {
      const { Purchases: RC } = await loadRevenueCat();
      if (!RC) return;

      const result = await RC.getCustomerInfo();
      const customerInfo = result.customerInfo;
      const isPremium = customerInfo?.entitlements?.active?.[PREMIUM_ENTITLEMENT] !== undefined;

      setState(prev => ({
        ...prev,
        customerInfo,
        isPremium,
      }));
    } catch (error: any) {
      console.error('RevenueCat: Refresh error:', error);
    }
  }, []);

  // Get monthly package
  const monthlyPackage = state.packages.find(
    (pkg: Package) => pkg.packageType === 'MONTHLY' || pkg.identifier === '$rc_monthly'
  );

  // Get annual package
  const annualPackage = state.packages.find(
    (pkg: Package) => pkg.packageType === 'ANNUAL' || pkg.identifier === '$rc_annual'
  );

  return {
    ...state,
    monthlyPackage,
    annualPackage,
    login,
    logout,
    purchasePackage,
    restorePurchases,
    refreshCustomerInfo,
  };
}

export default useRevenueCat;
